import { computed, inject } from '@angular/core';
import {
  FfdNotesInvitationJoinRequestStatus,
  NotesBaaSignRequest,
  NotesBaaSignRequestsDataService,
  NotesContractSource,
  NotesContractsDataService,
} from '@ffd/api-client';
import { ToastService } from '@libs/shared-services';
import { authActions, selectCurrentUserProfile } from '@libs/store';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { exhaustMap, pipe, switchMap, tap } from 'rxjs';
import { BaaSignModalState, BaaSignModalStep } from './baa-sign-modal.types';

const initialState: BaaSignModalState = {
  step: BaaSignModalStep.AuthorizedAgreement,
  signerFullName: '',
  signerEmail: '',
  isLoading: false,
  isLoadingSignRequest: true,
  activeSignRequest: null,
};

export const BaaSignModalStore = signalStore(
  withState(initialState),
  withProps(() => {
    const ngrxStore = inject(Store);

    return {
      _notesContractsDataService: inject(NotesContractsDataService),
      _notesBaaSignRequestsDataService: inject(NotesBaaSignRequestsDataService),
      _ngrxStore: ngrxStore,
      _toastService: inject(ToastService),
      _translateService: inject(TranslateService),
      _profileSignal: ngrxStore.selectSignal(selectCurrentUserProfile),
    };
  }),
  withComputed((store) => ({
    signRequestSent: computed(() => store.activeSignRequest() !== null),
    pendingSignerName: computed(() => store.activeSignRequest()?.signerName ?? ''),
    pendingSignerEmail: computed(() => store.activeSignRequest()?.signerEmail ?? ''),
    pendingInvitation: computed(() => {
      const invitations = store._profileSignal()?.ffdNotesInvitations ?? [];
      return invitations.find((invitation) => invitation.status === FfdNotesInvitationJoinRequestStatus.AWAITING_APPROVAL) ?? null;
    }),
  })),
  withComputed((store) => ({
    hasPendingInvitation: computed(() => store.pendingInvitation() !== null),
    pendingInvitationSiteName: computed(() => store.pendingInvitation()?.siteName ?? ''),
  })),
  withMethods((store) => {
    const handleError = () => {
      patchState(store, { isLoading: false, isLoadingSignRequest: false });
      store._toastService.error(store._translateService.instant('common_error_message'));
    };

    return {
      setInitialStep(step: BaaSignModalStep): void {
        patchState(store, { step });
      },
      goToAuthorizedAgreement(): void {
        patchState(store, { step: BaaSignModalStep.AuthorizedAgreement });
      },
      goToNotAuthorizedSignerDetails(): void {
        patchState(store, { step: BaaSignModalStep.NotAuthorizedSignerDetails });
      },
      resetModalState(): void {
        patchState(store, { step: BaaSignModalStep.AuthorizedAgreement, isLoading: false, signerFullName: '', signerEmail: '' });
      },
      loadActiveSignRequest: rxMethod<void>(
        pipe(
          switchMap(() =>
            store._notesBaaSignRequestsDataService.getActiveNotesBaaSignRequest().pipe(
              tapResponse({
                next: ({ items }) => {
                  patchState(store, { activeSignRequest: items[0] ?? null, isLoadingSignRequest: false });
                },
                error: handleError,
              }),
            ),
          ),
        ),
      ),
      sendSignerRequest: rxMethod<{ fullName: string; email: string }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          exhaustMap(({ fullName, email }) =>
            store._notesBaaSignRequestsDataService.createNotesBaaSignRequest({ signerName: fullName, signerEmail: email }).pipe(
              tapResponse({
                next: (activeSignRequest: NotesBaaSignRequest) => {
                  patchState(store, {
                    isLoading: false,
                    step: BaaSignModalStep.NotAuthorizedSuccess,
                    signerFullName: fullName,
                    signerEmail: email,
                    activeSignRequest,
                  });
                },
                error: handleError,
              }),
            ),
          ),
        ),
      ),
      submitBaaForm: rxMethod<{
        legalName: string;
        address: string;
        city: string;
        state: string;
        zip: string;
        signerFullName: string;
        signerEmail: string;
      }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          exhaustMap(({ legalName, address, city, state, zip, signerFullName, signerEmail }) =>
            store._notesContractsDataService
              .createNotesContract({
                legalName,
                address,
                city,
                state,
                zip,
                country: 'US',
                signatoryName: signerFullName,
                signatoryEmail: signerEmail,
                source: NotesContractSource.WEB,
              })
              .pipe(
                tapResponse({
                  next: () => {
                    store._ngrxStore.dispatch(authActions.onProfileRequested());
                    patchState(store, { isLoading: false, step: BaaSignModalStep.AuthorizedSuccess, signerFullName, signerEmail });
                  },
                  error: handleError,
                }),
              ),
          ),
        ),
      ),
    };
  }),
);
